import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  cn,
  Icon,
  Input,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  UserAvatar,
} from "@nexus/ui";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { EVENT_FILTER_ALL } from "../constants/event-filter-options";
import { toggleFilterValue } from "../lib/event-filter-csv";

/** Search drives a server query now, so typing must not fire one per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

export interface FilterOption {
  value: string;
  label: string;
  photoUrl?: string | null;
}

interface EventsFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: string[];
  onStatusChange: (value: string[]) => void;
  statusOptions: ReadonlyArray<FilterOption>;
  /**
   * Off for a view that has no per-row status to narrow by. A control that
   * silently does nothing is the same class of lie as a duration that omits
   * the queue.
   */
  showStatusFilter?: boolean;
  topic: string[];
  onTopicChange: (value: string[]) => void;
  topicOptions: ReadonlyArray<FilterOption>;
  eventTypes: string[];
  onEventTypeChange: (value: string[]) => void;
  eventTypeOptions: ReadonlyArray<FilterOption>;
  outcome: string;
  onOutcomeChange: (value: string) => void;
  outcomeOptions: ReadonlyArray<FilterOption>;
  subjectKind: string;
  onSubjectKindChange: (value: string) => void;
  subjectKindOptions: ReadonlyArray<FilterOption>;
  actorUserIds: string[];
  onActorUserChange: (value: string[]) => void;
  userOptions: ReadonlyArray<FilterOption>;
  hidePings: boolean;
  onHidePingsChange: (value: boolean) => void;
  healthPingCount: number | undefined;
  /** Set when a bucket is selected in the activity chart; null when it is not. */
  rangeLabel: string | null;
  onClearRange: () => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  resultCount: number | null;
  /** What the count counts, so the total names the rows actually on screen. */
  resultUnit?: "events" | "actions";
  isRefreshing: boolean;
  onRefresh: () => void;
  /** Trailing actions — rendered next to refresh, ahead of it in the tab order. */
  actions?: ReactNode;
}

const CONTROL_HEIGHT =
  "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:h-8 [@media(hover:hover)_and_(pointer:fine)]:min-h-8";
const FILTER_TRIGGER_CLASS =
  "min-h-11 w-[150px] justify-between rounded-sm border border-input-border bg-input-background px-3 py-1 text-xs font-normal text-foreground shadow-none hover:bg-input-background [@media(hover:hover)_and_(pointer:fine)]:h-8 [@media(hover:hover)_and_(pointer:fine)]:min-h-8";
const ACTIVE_TRIGGER_CLASS = "border-primary/40 bg-primary/5";

function FilterSelect({
  value,
  onValueChange,
  label,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  options: ReadonlyArray<FilterOption>;
}) {
  const isActive = value !== EVENT_FILTER_ALL;

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={label}
        className={cn(FILTER_TRIGGER_CLASS, isActive && ACTIVE_TRIGGER_CLASS)}
      >
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FilterMultiSelect({
  values,
  onValuesChange,
  label,
  options,
  searchPlaceholder,
  emptyMessage,
  showUserAvatar = false,
}: {
  values: string[];
  onValuesChange: (next: string[]) => void;
  label: string;
  options: ReadonlyArray<FilterOption>;
  searchPlaceholder: string;
  emptyMessage: string;
  showUserAvatar?: boolean;
}) {
  const { t } = useTranslation();
  const isActive = values.length > 0;
  const selectedLabel =
    values.length === 0
      ? label
      : values.length === 1
        ? (options.find((option) => option.value === values[0])?.label ?? values[0]!)
        : t("admin.events.filters.selectedCount", "{{count}} selected", {
            count: values.length,
          });

  return (
    <SearchableCombobox<FilterOption>
      items={[...options]}
      values={values}
      onSelect={(option) => onValuesChange(toggleFilterValue(values, option.value))}
      getKey={(option) => option.value}
      getLabel={(option) => option.label}
      getSearchText={(option) => `${option.label} ${option.value}`}
      placeholder={label}
      ariaLabel={values.length === 0 ? label : `${label}: ${selectedLabel}`}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      getSelectedLabel={(selected) =>
        selected.length === 1
          ? selected[0]!.label
          : t("admin.events.filters.selectedCount", "{{count}} selected", {
              count: selected.length,
            })
      }
      triggerClassName={cn(FILTER_TRIGGER_CLASS, isActive && ACTIVE_TRIGGER_CLASS)}
      contentClassName="min-w-[220px]"
      renderItem={(option) => (
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {showUserAvatar ? (
            <UserAvatar
              name={option.label}
              src={option.photoUrl}
              size="sm"
              className="shrink-0"
            />
          ) : null}
          <span className="min-w-0 truncate">{option.label}</span>
        </span>
      )}
    />
  );
}

export function EventsFilterBar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  statusOptions,
  showStatusFilter = true,
  topic,
  onTopicChange,
  topicOptions,
  eventTypes,
  onEventTypeChange,
  eventTypeOptions,
  outcome,
  onOutcomeChange,
  outcomeOptions,
  subjectKind,
  onSubjectKindChange,
  subjectKindOptions,
  actorUserIds,
  onActorUserChange,
  userOptions,
  hidePings,
  onHidePingsChange,
  healthPingCount,
  rangeLabel,
  onClearRange,
  hasActiveFilters,
  onClearFilters,
  resultCount,
  resultUnit = "events",
  isRefreshing,
  onRefresh,
  actions,
}: EventsFilterBarProps) {
  const { t, i18n } = useTranslation();
  const [searchInput, setSearchInput] = useState(search);
  const { debounced: debouncedSearchChange, cancel: cancelSearchChange } =
    useDebouncedCallback(onSearchChange, SEARCH_DEBOUNCE_MS);

  // Re-sync when the committed value changes elsewhere (Clear filters, back/forward).
  useEffect(() => {
    setSearchInput(search);
    cancelSearchChange();
  }, [search, cancelSearchChange]);

  const noMatch = t("admin.events.filters.noMatch", "No matches.");

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <div className="relative flex items-center">
        <Icon
          name="search"
          size="xs"
          className="pointer-events-none absolute left-2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            debouncedSearchChange(event.target.value);
          }}
          aria-label={t("admin.events.filters.searchLabel", "Search events")}
          placeholder={t(
            "admin.events.filters.searchPlaceholder",
            "Order ID, correlation ID, or event type…",
          )}
          className={cn(
            CONTROL_HEIGHT,
            "w-[220px] rounded-sm pl-7 text-xs focus-visible:ring-0",
          )}
        />
      </div>

      {showStatusFilter ? (
        <FilterMultiSelect
          values={status}
          onValuesChange={onStatusChange}
          label={t("admin.events.filters.status", "All statuses")}
          options={statusOptions}
          searchPlaceholder={t("admin.events.filters.searchStatuses", "Search statuses…")}
          emptyMessage={noMatch}
        />
      ) : null}
      <FilterMultiSelect
        values={eventTypes}
        onValuesChange={onEventTypeChange}
        label={t("admin.events.filters.eventType", "All types")}
        options={eventTypeOptions}
        searchPlaceholder={t("admin.events.filters.searchTypes", "Search types…")}
        emptyMessage={noMatch}
      />
      <FilterSelect
        value={outcome}
        onValueChange={onOutcomeChange}
        label={t("admin.events.filters.outcome", "All outcomes")}
        options={outcomeOptions}
      />
      <FilterSelect
        value={subjectKind}
        onValueChange={onSubjectKindChange}
        label={t("admin.events.filters.subjectKind", "All subjects")}
        options={subjectKindOptions}
      />
      <FilterMultiSelect
        values={topic}
        onValuesChange={onTopicChange}
        label={t("admin.events.filters.topic", "All topics")}
        options={topicOptions}
        searchPlaceholder={t("admin.events.filters.searchTopics", "Search topics…")}
        emptyMessage={noMatch}
      />
      <FilterMultiSelect
        values={actorUserIds}
        onValuesChange={onActorUserChange}
        label={t("admin.events.filters.user", "All users")}
        options={userOptions}
        searchPlaceholder={t("admin.events.filters.searchUsers", "Search users…")}
        emptyMessage={noMatch}
        showUserAvatar
      />

      <label
        className={cn(
          CONTROL_HEIGHT,
          "inline-flex cursor-pointer select-none items-center gap-1.5 rounded-sm border border-input-border bg-card px-2.5 text-xs text-muted-foreground",
        )}
      >
        <Checkbox
          checked={hidePings}
          onCheckedChange={(checked) => onHidePingsChange(checked === true)}
          className="size-3.5"
        />
        {healthPingCount == null
          ? t("admin.events.filters.hidePings", "Hide health pings")
          : t("admin.events.filters.hidePingsWithCount", "Hide health pings ({{count}})", {
              count: healthPingCount,
            })}
      </label>

      {/* The chart sets this filter, so the bar is where it becomes visible and
          removable — otherwise a click on a bar looks like the list broke. */}
      {rangeLabel ? (
        <span
          className={cn(
            CONTROL_HEIGHT,
            "inline-flex items-center gap-1.5 rounded-sm border border-primary/40 bg-primary/5 pl-2 pr-1 text-xs text-foreground",
          )}
        >
          <Icon name="filter" size="xs" className="shrink-0 text-primary" aria-hidden />
          {rangeLabel}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-5 rounded-sm"
            aria-label={t("admin.events.filters.rangeClear", "Clear the selected time span")}
            onClick={onClearRange}
          >
            <Icon name="x" size="xs" aria-hidden />
          </Button>
        </span>
      ) : null}

      {hasActiveFilters ? (
        <Button variant="ghost" size="sm" className={cn(CONTROL_HEIGHT, "text-xs text-primary")} onClick={onClearFilters}>
          {t("admin.events.filters.clear", "Clear filters")}
        </Button>
      ) : null}

      <span className="flex-1" />

      {resultCount == null ? null : (
        <span className="text-xs tabular-nums text-muted-foreground">
          {resultUnit === "actions"
            ? t("admin.events.filters.resultCountActions", "{{count}} actions", {
                count: resultCount,
                formattedCount: resultCount.toLocaleString(i18n.language),
              })
            : t("admin.events.filters.resultCount", "{{count}} events", {
                count: resultCount,
                formattedCount: resultCount.toLocaleString(i18n.language),
              })}
        </span>
      )}

      {actions}

      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        className="size-8 min-h-11 min-w-11 shrink-0 rounded-sm [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
        aria-label={t("admin.events.filters.refresh", "Refresh list")}
        disabled={isRefreshing}
        onClick={onRefresh}
      >
        <Icon
          name="refresh-cw"
          size="xs"
          className={cn(isRefreshing && "animate-spin motion-reduce:animate-none")}
          aria-hidden
        />
      </Button>
    </div>
  );
}
